const BASE = (import.meta as any).env?.VITE_API_BASE || '/api';

let authToken: string | null = localStorage.getItem('auth_token');

export function setToken(token: string | null) {
  authToken = token;
  if (token) localStorage.setItem('auth_token', token);
  else localStorage.removeItem('auth_token');
}

export function getToken() { return authToken; }

export function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers = { ...extra };
  if (authToken) headers['Authorization'] = 'Bearer ' + authToken;
  return headers;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers = authHeaders({ 'Content-Type': 'application/json' });
  const res = await fetch(BASE + path, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Request failed');
  return json.data as T;
}

export const api = {
  getCaptcha: () => request<{ id: string; question: string }>('GET', '/auth/captcha'),
  login: (username: string, password: string, captchaId?: string, captchaAnswer?: string) =>
    request<{ token: string; username: string }>('POST', '/auth/login', { username, password, captchaId, captchaAnswer }),
  logout: () => request('POST', '/auth/logout'),
  authStatus: () => request<{ authenticated: boolean; username: string }>('GET', '/auth/status'),

  getRoutes: () => request<any[]>('GET', '/routes'),
  getRouteStats: () => request<Record<string, any>>('GET', '/routes/stats'),
  getTradeSummaryStats: () => request<Record<string, any>>('GET', '/routes/summary-stats'),
  getRouteSpreadHistory: (params: { routeId?: number; token?: string; since?: string; limit?: number; offset?: number; bucketSec?: number } = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
    }
    return request<any[]>('GET', '/routes/spread-history' + (search.toString() ? '?' + search.toString() : ''));
  },
  updateRoute: (id: number, data: any) => request('PATCH', '/routes/' + id, data),

  getTrades: (limit = 50) => request<any[]>('GET', '/trades?limit=' + encodeURIComponent(String(limit))),
  getTrade: (id: number) => request('GET', '/trades/' + id),

  getObservations: (all = false) => request<any[]>('GET', '/observations' + (all ? '?all=true' : '')),

  getBalances: () => request<any[]>('GET', '/balances'),
  getBalanceHistory: (period: string) =>
    request<any>('GET', '/balances/history?period=' + encodeURIComponent(period)),
  getBalancesByPlatform: (platform: string) => request<any[]>('GET', '/balances/platform/' + platform),
  getBalanceBaselines: () => request<any[]>('GET', '/balances/baselines'),
  updateBalanceBaseline: (token: string, amount: string) =>
    request('PUT', '/balances/baselines/' + encodeURIComponent(token), { amount }),
  getBalanceDepositAddresses: (token = 'USDT', network = 'TON') =>
    request<any>('GET', '/balances/deposit-addresses?token=' + encodeURIComponent(token) + '&network=' + encodeURIComponent(network)),
  getBalanceNetworkAvailability: (tokens = 'GRAM,USDT,EVAA,DOGS,UTYA,NOT,STORM,REDO') =>
    request<any>('GET', '/balances/network-availability?tokens=' + encodeURIComponent(tokens)),

  getSettings: () => request<Record<string, string>>('GET', '/settings'),
  updateSettings: (data: Record<string, string>) => request('PUT', '/settings', data),
  getSecrets: () => request<Record<string, string>>('GET', '/settings/secrets'),
  updateSecrets: (data: Record<string, string>) => request('PUT', '/settings/secrets', data),

  getScannerStatus: () => request<any>('GET', '/scanner/status'),
  triggerScan: () => request('POST', '/scanner/trigger'),
  startScanner: () => request('POST', '/scanner/start'),
  stopScanner: () => request('POST', '/scanner/stop'),

  getAutoBalanceConfigs: () => request<any[]>('GET', '/auto-balance/configs'),
  updateAutoBalanceConfig: (token: string, data: any) => request('PUT', '/auto-balance/configs/' + token, data),
  getAutoBalanceStatus: () => request<any[]>('GET', '/auto-balance/status'),
  checkAutoBalance: () => request<any>('POST', '/auto-balance/check'),
  getAutoBalanceTransfers: () => request<any[]>('GET', '/auto-balance/transfers'),
  getAutoBalanceDepositAddresses: (token = 'USDT', network = 'TON') =>
    request<any>('GET', '/auto-balance/deposit-addresses?token=' + encodeURIComponent(token) + '&network=' + encodeURIComponent(network)),

  getSystemStatus: () => request<any>('GET', '/system/status'),
  killSwitchOn: () => request('POST', '/system/kill-switch'),
  killSwitchOff: () => request('POST', '/system/kill-switch/off'),

  getAuditTrades: (params: { limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) search.set(key, String(value));
    }
    return request<{ trades: any[]; total: number; limit: number; offset: number; nextOffset: number; hasMore: boolean }>(
      'GET',
      '/audit/trades' + (search.toString() ? '?' + search.toString() : '')
    );
  },

  getTokenRiskConfigs: () => request<any[]>('GET', '/token-risk'),
  updateTokenRiskConfig: (token: string, data: any) => request('PUT', '/token-risk/' + encodeURIComponent(token), data),

  getDexQuote: (data: any) => request<any>('POST', '/dex/quote', data),
  getDexWalletStatus: () => request<any>('GET', '/dex/wallet-status'),
  getManualRecovery: () => request<any>('GET', '/dex/recovery'),
  prepareManualRecovery: (id: number) => request<any>('POST', `/dex/recovery/${id}/prepare`, {}),
  executeManualRecovery: (id: number, data: { quoteId: string; confirmation: string; acceptLossAboveLimit: boolean }) =>
    request<any>('POST', `/dex/recovery/${id}/execute`, data),
  prepareDexSwap: (data: any) => request<any>('POST', '/dex/prepare-swap', data),
  executeDexSwap: (data: any) => request<any>('POST', '/dex/execute-swap', data),
  getDexLimitOrders: () => request<any[]>('GET', '/dex/limit-orders'),
  getDexLimitOrderAttempts: (id: number) => request<any[]>('GET', '/dex/limit-orders/' + id + '/attempts'),
  createDexLimitOrder: (data: any) => request<any>('POST', '/dex/limit-orders', data),
  updateDexLimitOrder: (id: number, data: any) =>
    request<any>('PATCH', '/dex/limit-orders/' + id, data),
};
